import { ModelResource } from './resource';
import { Deduction, DeductionRemittance } from '../models';
import { downloadBlob } from '@ui/utils/utils';
import { DeductionQueryParams } from '../query';

export interface RemittancesResult {
  /** Unbounded count of active remittances matching the filter (may exceed `data.length`). */
  total_active: number;
  /**
   * Remittances most-recently-received first, active ones before fully-completed
   * ones. Capped at `params.limit` (default 50, max 100).
   */
  data: DeductionRemittance[];
}

export class Deductions extends ModelResource<Deduction> {
  model = Deduction;

  resourcePath = 'v1/deductions/deductions';

  /**
   * List remittances most-recently-received first, with active ones (at least one
   * not-yet-completed deduction) before fully-completed ones. `params.customer`
   * (partner IDs) scopes the result and its counts identically to the deductions
   * list customer filter; `params.limit` caps the count (default 50, max 100).
   */
  async listRemittances(params?: Record<string, any>): Promise<RemittancesResult> {
    const response = await this.api.get('v1/deductions/remittances', { params });
    return {
      total_active: response.data.total_active ?? 0,
      data: (response.data.data ?? []).map((json: any) => DeductionRemittance.fromJson(json)),
    };
  }

  async assignClaim(id: string, claimId, deduction: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { claim_id: claimId },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async export(params: Record<string, any>) {
    const response = await this.api.get('v1/deductions/deductions-export', {
      params,
      responseType: 'blob',
    });

    return downloadBlob(response.data, 'deductions-export.csv', 'text/csv');
  }

  async exportCreditMemos(params: Record<string, any>) {
    const response = await this.api.get('v1/deductions/credit-memos-export', {
      params,
      responseType: 'blob',
    });

    return downloadBlob(response.data, 'credit-memos-export.csv', 'text/csv');
  }

  async updateTotal(id: string, amount, deduction: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { amount },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async updateReason(id: string, reason_id, deduction: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { reason_id },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async update(id: string, data, deduction: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data,
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async reprocess(id: string, deduction?: Deduction, userInstructions?: string) {
    const response = await this.api.post(this.path(id, 'reprocess'), {
      data: userInstructions ? { user_instructions: userInstructions } : undefined,
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async rematch(id: string, deduction?: Deduction) {
    const response = await this.api.post(this.path(id, 'rematch'));

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async getVendors() {
    const vendors = await this.api.get(this.path('deductions/vendors'));
    return vendors;
  }

  async getCustomers(params?: Record<string, any>) {
    const response = await this.api.get('v1/deductions/customers', { params });
    return response.data;
  }

  async getCustomFieldOptions(fieldName: string) {
    const response = await this.api.get(`v1/deductions/custom-field-options/${fieldName}`);
    return response.data.options;
  }

  async markSeen(id: string): Promise<void> {
    await this.api.client.put(this.path(id, 'seen'));
  }

  async approve(id: string, deduction?: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { approval_status: 'approved' },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async reject(id: string, deduction?: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { approval_status: 'rejected' },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async clearApproval(id: string, deduction?: Deduction) {
    const response = await this.api.patch(this.path(id), {
      data: { approval_status: 'pending' },
    });

    if (deduction) {
      deduction.updateFromJson(response.data);
    } else {
      deduction = Deduction.fromJson(response.data);
    }

    return deduction;
  }

  async getApprovalStats(params?: Record<string, any>): Promise<{
    approved: number;
    rejected: number;
    pending: number;
  }> {
    const response = await this.api.get('v1/deductions/approval-stats', { params });
    return response.data;
  }
}
